import React from "react";
import {
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  Text,
  View,
} from "react-native";

import type { TNormalizedClinicAsset } from "../../../common/hooks/clinicAssets";
import { PrimaryButton, SecondaryButton } from "../../../common/native";
import ImageFilterIcon, {
  TImageFilterIconName,
} from "../../../common/web/ImageFilterIcon.web";
import type { TCensorshipJobMetadata } from "../../../../services/api-image-tools";
import { APP_TOKENS as tokens } from "../../../../styles/tokens";
import type { TCensorshipStickerPlacement } from "./CensorshipResultEditor.web";
import type { TFaceBlurRegion } from "./FaceBlurResultEditor.web";
import { styles } from "./ImageToolsPage.styles";
import { IMAGE_TOOLS_TOKENS as imageToolsTokens } from "./tokens";
import UnifiedImageToolsEditor from "./UnifiedImageToolsEditor.web";

const IMAGE_FILTER_OPTIONS = [
  {
    id: "blur-tattoos",
    name: "Blur Tattoos",
    icon: "tattoo" as const,
    available: true,
  },
  {
    id: "remove-background",
    name: "Remove Background",
    icon: "remove-background" as const,
    available: true,
  },
  {
    id: "hide-faces",
    name: "Face censorship",
    icon: "face" as const,
    available: true,
  },
  {
    id: "cover-privates",
    name: "Cover Privates",
    icon: "privacy" as const,
    available: true,
  },
] as const;

export type TImageFilterId = (typeof IMAGE_FILTER_OPTIONS)[number]["id"];
export type TFaceCensorshipMode = "sticker" | "blur";

export type TCoverOption = {
  id: string;
  label: string;
  url: string;
  assetId?: number;
};

export type TImageToolsJobResult = {
  batchId: string;
  downloadUrls: string[];
  metadata: TCensorshipJobMetadata;
  appliedFilters: TImageFilterId[];
  coverImageUrl?: string;
  coverImageLabel?: string;
  coverAssetId?: number;
  faceCensorshipMode?: TFaceCensorshipMode;
};

const FILTER_RESULT_LABELS: Record<TImageFilterId, string> = {
  "blur-tattoos": "tattoos blurred",
  "remove-background": "background removed",
  "hide-faces": "faces hidden",
  "cover-privates": "private areas covered",
};

const ImageFilterOption: React.FC<{
  id: TImageFilterId;
  name: string;
  icon: TImageFilterIconName;
  available: boolean;
  disabled: boolean;
  selected: boolean;
  mobile: boolean;
  onSelect: () => void;
}> = ({ id, name, icon, available, disabled, selected, mobile, onSelect }) => {
  const [focused, setFocused] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);

  return (
    <Pressable
      testID={`image-filter-${id}`}
      accessibilityRole="checkbox"
      accessibilityLabel={`${name}${available ? "" : ", coming soon"}`}
      accessibilityState={{ disabled, checked: selected }}
      aria-checked={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onPress={onSelect}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [
        styles.filterOption,
        mobile && styles.mobileFilterOption,
        selected && styles.filterOptionSelected,
        hovered && !disabled && styles.filterOptionHovered,
        focused && styles.focused,
        pressed && !disabled && styles.filterOptionPressed,
        !available && styles.filterOptionUnavailable,
        disabled && available && styles.disabled,
      ]}
    >
      {!available ? (
        <View
          style={styles.comingSoonBadge}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <Text style={styles.comingSoonBadgeText}>Coming soon</Text>
        </View>
      ) : null}
      <View
        style={[
          styles.filterOptionIcon,
          mobile && styles.mobileFilterOptionIcon,
        ]}
      >
        <ImageFilterIcon
          name={icon}
          color={available ? tokens.color.textPrimary : tokens.color.textMuted}
          size={mobile ? tokens.fontSize.xl : tokens.fontSize.xxxl}
        />
      </View>
      <Text
        style={[styles.filterOptionText, mobile && styles.mobileFilterText]}
      >
        {name}
      </Text>
    </Pressable>
  );
};

export const ImageToolsFilterPicker: React.FC<{
  selectedFilters: TImageFilterId[];
  disabled: boolean;
  isMobile: boolean;
  onToggleFilter: (filterId: TImageFilterId) => void;
}> = ({ selectedFilters, disabled, isMobile, onToggleFilter }) => (
  <>
    <View style={styles.divider} />

    <View style={styles.sectionHeader}>
      <View style={styles.stepBadge}>
        <Text style={styles.stepBadgeText}>2</Text>
      </View>
      <View style={styles.sectionHeaderCopy}>
        <Text accessibilityRole="header" style={styles.sectionTitle}>
          What filters do you want to apply?
        </Text>
        <Text style={styles.sectionDescription}>
          Select one or more filters. They will be combined in a single secure
          processing job.
        </Text>
      </View>
    </View>

    <View accessibilityLabel="Image filters" style={styles.filterOptionsRow}>
      {IMAGE_FILTER_OPTIONS.map((filter) => (
        <ImageFilterOption
          key={filter.id}
          id={filter.id}
          name={filter.name}
          icon={filter.icon}
          available={filter.available}
          disabled={!filter.available || disabled}
          selected={selectedFilters.includes(filter.id)}
          mobile={isMobile}
          onSelect={() => onToggleFilter(filter.id)}
        />
      ))}
    </View>
  </>
);

const FaceCensorshipModeOption: React.FC<{
  option: TFaceCensorshipMode;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}> = ({ option, selected, disabled, onSelect }) => {
  const [focused, setFocused] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);

  return (
    <Pressable
      testID={`face-censorship-mode-${option}`}
      accessibilityRole="radio"
      accessibilityLabel={`${option === "sticker" ? "Sticker" : "Blur"} face censorship mode`}
      accessibilityState={{ checked: selected, disabled }}
      aria-checked={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onPress={onSelect}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [
        styles.faceModeOption,
        selected && styles.faceModeOptionSelected,
        hovered && !disabled && styles.interactiveHover,
        focused && styles.focused,
        pressed && !disabled && styles.pressed,
        disabled && styles.disabled,
      ]}
    >
      <Text style={styles.faceModeOptionText}>
        {option === "sticker" ? "Sticker" : "Blur"}
      </Text>
      <Text style={styles.faceModeOptionDescription}>
        {option === "sticker"
          ? "Place covers yourself"
          : "Detect, review, and adjust"}
      </Text>
    </Pressable>
  );
};

export const FaceCensorshipModePicker: React.FC<{
  mode: TFaceCensorshipMode;
  disabled: boolean;
  onChange: (mode: TFaceCensorshipMode) => void;
}> = ({ mode, disabled, onChange }) => (
  <View style={styles.faceModeSection}>
    <View style={styles.sectionHeaderCopy}>
      <Text accessibilityRole="header" style={styles.stickerLibraryTitle}>
        Face censorship mode
      </Text>
      <Text style={styles.sectionDescription}>
        Blur proposes detected regions for review. Sticker lets you place one or
        more covers manually. Face censorship can be combined with every Image
        Tools filter; all selected effects are rendered after review.
      </Text>
    </View>
    <View accessibilityRole="radiogroup" style={styles.faceModeOptions}>
      {(["sticker", "blur"] as const).map((option) => (
        <FaceCensorshipModeOption
          key={option}
          option={option}
          selected={mode === option}
          disabled={disabled}
          onSelect={() => onChange(option)}
        />
      ))}
    </View>
  </View>
);

const CoverOption: React.FC<{
  id: string;
  label: string;
  url: string;
  selected: boolean;
  disabled: boolean;
  deleting: boolean;
  onSelect: () => void;
  onDelete?: () => void;
}> = ({ id, label, url, selected, disabled, deleting, onSelect, onDelete }) => {
  const [focused, setFocused] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [deleteFocused, setDeleteFocused] = React.useState(false);
  const [deleteHovered, setDeleteHovered] = React.useState(false);

  return (
    <View style={styles.coverOptionWrapper}>
      <Pressable
        testID={`sticker-option-${id}`}
        accessibilityRole="radio"
        accessibilityLabel={`${label} censorship cover`}
        accessibilityState={{ checked: selected, disabled }}
        aria-checked={selected}
        aria-disabled={disabled}
        disabled={disabled}
        onPress={onSelect}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        style={[
          styles.coverOption,
          onDelete && styles.coverOptionWithDelete,
          hovered && !disabled && styles.interactiveHover,
          selected && styles.coverOptionSelected,
          focused && styles.focused,
          disabled && styles.disabled,
        ]}
      >
        <View style={styles.coverPreviewContainer}>
          <Image
            source={{ uri: url }}
            style={styles.coverPreview}
            resizeMode="contain"
            accessible
            accessibilityLabel={`${label} sticker preview`}
          />
        </View>
        <Text style={styles.coverLabel}>{label}</Text>
        <View
          style={[
            styles.radioOuter,
            onDelete && styles.customStickerRadio,
            selected && styles.radioOuterSelected,
          ]}
        >
          {selected ? <View style={styles.radioInner} /> : null}
        </View>
      </Pressable>
      {onDelete ? (
        <Pressable
          testID={`delete-sticker-${id}`}
          accessibilityRole="button"
          accessibilityLabel={`Delete ${label}`}
          accessibilityState={{ disabled: disabled || deleting }}
          aria-disabled={disabled || deleting}
          disabled={disabled || deleting}
          onPress={onDelete}
          onFocus={() => setDeleteFocused(true)}
          onBlur={() => setDeleteFocused(false)}
          onHoverIn={() => setDeleteHovered(true)}
          onHoverOut={() => setDeleteHovered(false)}
          style={({ pressed }) => [
            styles.deleteStickerButton,
            deleteHovered && !disabled && styles.deleteStickerButtonHovered,
            deleteFocused && styles.focused,
            pressed && styles.pressed,
            (disabled || deleting) && styles.disabled,
          ]}
        >
          {deleting ? (
            <ActivityIndicator size="small" color={tokens.color.inverse} />
          ) : (
            <ImageFilterIcon
              name="delete"
              color={tokens.color.inverse}
              size={tokens.fontSize.md}
            />
          )}
        </Pressable>
      ) : null}
    </View>
  );
};

export const ImageToolsStickerLibrary: React.FC<{
  coverOptions: TCoverOption[];
  selectedCoverId: string;
  stickerToDelete: TNormalizedClinicAsset | null;
  isBusy: boolean;
  isMobile: boolean;
  isLoading: boolean;
  isAdding: boolean;
  deletingAssetId: number | null;
  error: string;
  onAddSticker: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectCover: (coverId: string) => void;
  onRequestDelete: (assetId: number) => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onRetry: () => void;
  purpose?: "private-areas" | "faces" | "both";
}> = ({
  coverOptions,
  selectedCoverId,
  stickerToDelete,
  isBusy,
  isMobile,
  isLoading,
  isAdding,
  deletingAssetId,
  error,
  onAddSticker,
  onSelectCover,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onRetry,
  purpose = "private-areas",
}) => {
  const stickerInputRef = React.useRef<HTMLInputElement>(null);

  return (
    <>
      <View style={styles.divider} />

      <View style={styles.sectionHeader}>
        <View style={styles.stepBadge}>
          <Text style={styles.stepBadgeText}>3</Text>
        </View>
        <View style={styles.sectionHeaderCopy}>
          <Text accessibilityRole="header" style={styles.sectionTitle}>
            {purpose === "faces"
              ? "Choose a face sticker"
              : purpose === "both"
                ? "Choose a censorship sticker"
                : "Choose a cover"}
          </Text>
          <Text style={styles.sectionDescription}>
            {purpose === "faces"
              ? "Choose a preset or clinic sticker, then place one or more covers over faces in the review step."
              : purpose === "both"
                ? "Choose a preset or clinic sticker for face and private-area placements. You can review every cover before export."
                : "Choose a preset or a sticker saved in your clinic library. The selected sticker is placed over every private area the AI detects."}
          </Text>
        </View>
      </View>

      <View
        style={[styles.stickerLibraryToolbar, isMobile && styles.mobileStack]}
      >
        <View style={styles.stickerLibraryCopy}>
          <Text style={styles.stickerLibraryTitle}>Sticker library</Text>
          <Text style={styles.sectionDescription}>
            Add reusable clinic stickers here. Deleting one removes it from the
            shared clinic asset library.
          </Text>
        </View>
        <SecondaryButton
          text="Add Sticker"
          onPress={() => stickerInputRef.current?.click()}
          loading={isAdding}
          disabled={isBusy || isAdding}
          fullWidth={isMobile}
          icon={<ImageFilterIcon name="add" color={tokens.color.textPrimary} />}
        />
      </View>

      <input
        ref={stickerInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={onAddSticker}
        disabled={isBusy || isAdding}
        aria-label="Add a sticker to the clinic library"
        data-testid="image-tools-sticker-input"
        style={styles.hiddenInput as React.CSSProperties}
      />

      {error ? (
        <View style={styles.errorBanner}>
          <Text
            accessibilityLiveRegion="assertive"
            accessibilityRole="alert"
            style={styles.errorText}
          >
            {error}
          </Text>
          <SecondaryButton
            text="Retry"
            onPress={onRetry}
            disabled={isLoading}
            loading={isLoading}
            style={styles.retryButton}
          />
        </View>
      ) : null}

      {isLoading ? (
        <View
          accessibilityLabel="Loading sticker library"
          style={styles.libraryLoading}
        >
          <ActivityIndicator size="small" color={tokens.color.accent} />
          <Text
            accessibilityLiveRegion="polite"
            style={styles.sectionDescription}
          >
            Loading clinic stickers…
          </Text>
        </View>
      ) : (
        <View accessibilityRole="radiogroup" style={styles.coverGrid}>
          {coverOptions.map((cover) => (
            <CoverOption
              key={cover.id}
              id={cover.id}
              label={cover.label}
              url={cover.url}
              selected={selectedCoverId === cover.id}
              disabled={isBusy || deletingAssetId !== null}
              deleting={deletingAssetId === cover.assetId}
              onSelect={() => onSelectCover(cover.id)}
              onDelete={
                cover.assetId
                  ? () => onRequestDelete(cover.assetId as number)
                  : undefined
              }
            />
          ))}
        </View>
      )}

      <Modal
        visible={stickerToDelete !== null}
        transparent
        animationType="fade"
        onRequestClose={onCancelDelete}
      >
        <View style={styles.dialogBackdrop}>
          <View
            accessibilityRole="alert"
            accessibilityLabel="Delete clinic sticker"
            style={styles.dialog}
          >
            <Text accessibilityRole="header" style={styles.dialogTitle}>
              Delete clinic sticker?
            </Text>
            {stickerToDelete ? (
              <View style={styles.dialogPreview}>
                <Image
                  source={{ uri: stickerToDelete.get_url }}
                  style={styles.coverPreview}
                  resizeMode="contain"
                  accessible
                  accessibilityLabel="Sticker to delete"
                />
              </View>
            ) : null}
            <Text style={styles.sectionDescription}>
              This permanently removes the sticker from your clinic asset
              library, including the library used by Before/After.
            </Text>
            <View
              style={[styles.dialogActions, isMobile && styles.mobileActions]}
            >
              <SecondaryButton
                text="Cancel"
                onPress={onCancelDelete}
                disabled={deletingAssetId !== null}
                fullWidth={isMobile}
              />
              <SecondaryButton
                text="Delete Sticker"
                onPress={onConfirmDelete}
                loading={deletingAssetId !== null}
                disabled={deletingAssetId !== null}
                fullWidth={isMobile}
                style={styles.deleteAction}
                textStyle={styles.deleteActionText}
              />
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export const ImageToolsProcessingStatus: React.FC<{
  error: string;
  isBusy: boolean;
  isMobile: boolean;
  isUploading: boolean;
  uploadProgress: number;
  statusText: string;
  applyDisabled: boolean;
  onApply: () => void;
}> = ({
  error,
  isBusy,
  isMobile,
  isUploading,
  uploadProgress,
  statusText,
  applyDisabled,
  onApply,
}) => (
  <>
    {error ? (
      <View style={styles.errorBanner}>
        <Text
          accessibilityLiveRegion="assertive"
          accessibilityRole="alert"
          style={styles.errorText}
        >
          {error}
        </Text>
      </View>
    ) : null}

    {isBusy ? (
      <View style={styles.progressPanel}>
        <View style={styles.progressCopyRow}>
          <ActivityIndicator size="small" color={tokens.color.accent} />
          <Text accessibilityLiveRegion="polite" style={styles.progressText}>
            {statusText}
          </Text>
        </View>
        <View style={styles.progressTrack}>
          <View
            style={[
              styles.progressFill,
              {
                width: isUploading
                  ? `${uploadProgress}%`
                  : imageToolsTokens.size.progressComplete,
              },
            ]}
          />
        </View>
        <Text style={styles.progressHint}>
          Larger batches can take a few minutes. Keep this page open while
          processing finishes.
        </Text>
      </View>
    ) : null}

    <View style={[styles.actions, isMobile && styles.mobileActions]}>
      <PrimaryButton
        text="Apply Filters"
        onPress={onApply}
        loading={isBusy}
        disabled={applyDisabled}
        fullWidth={isMobile}
        style={isMobile ? null : styles.primaryAction}
      />
    </View>
  </>
);

const getAppliedFiltersDescription = (filters: TImageFilterId[]) =>
  filters.map((filter) => FILTER_RESULT_LABELS[filter]).join(", ");

export const ImageToolsResults: React.FC<{
  result: TImageToolsJobResult;
  imagePreviewUrls: string[];
  placementsByImage: Record<number, TCensorshipStickerPlacement[]>;
  faceStickerPlacementsByImage: Record<number, TCensorshipStickerPlacement[]>;
  faceBlurRegionsByImage: Record<number, TFaceBlurRegion[]>;
  downloadingIndex: number | null;
  isDownloadingAll: boolean;
  isHighQualityExporting: boolean;
  downloadDialogVisible: boolean;
  downloadDialogIsAll: boolean;
  editorsReady: boolean;
  isDebugMode: boolean;
  showRegionDebugLabels: boolean;
  isMobile: boolean;
  viewportWidth: number;
  onDownload: (index: number) => void;
  onDownloadAll: () => void;
  onCancelDownload: () => void;
  onRegularDownload: () => void;
  onHighQualityDownload: () => void;
  onPlacementsChange: (
    index: number,
    placements: TCensorshipStickerPlacement[],
    isEdited: boolean,
  ) => void;
  onFaceStickerPlacementsChange: (
    index: number,
    placements: TCensorshipStickerPlacement[],
    isEdited: boolean,
  ) => void;
  onFaceBlurRegionsChange: (
    index: number,
    regions: TFaceBlurRegion[],
    isEdited: boolean,
  ) => void;
}> = ({
  result,
  imagePreviewUrls,
  placementsByImage,
  faceStickerPlacementsByImage,
  faceBlurRegionsByImage,
  downloadingIndex,
  isDownloadingAll,
  isHighQualityExporting,
  downloadDialogVisible,
  downloadDialogIsAll,
  editorsReady,
  isDebugMode,
  showRegionDebugLabels,
  isMobile,
  viewportWidth,
  onDownload,
  onDownloadAll,
  onCancelDownload,
  onRegularDownload,
  onHighQualityDownload,
  onPlacementsChange,
  onFaceStickerPlacementsChange,
  onFaceBlurRegionsChange,
}) => {
  const hasFaceCensorship = result.appliedFilters.includes("hide-faces");
  const isEditableCensorship =
    result.appliedFilters.includes("cover-privates") &&
    Boolean(result.coverImageUrl);
  const hasEditableTools = isEditableCensorship || hasFaceCensorship;
  const requiresCompositeExport =
    hasFaceCensorship ||
    (result.appliedFilters.includes("cover-privates") &&
      result.appliedFilters.some((filter) => filter !== "cover-privates"));
  const faceReviewRequired =
    hasFaceCensorship &&
    (Boolean(result.metadata.errors) ||
      result.metadata.images.some((image) =>
        image.ai_filters?.face_censorship?.warnings?.some(
          (warning) =>
            warning === "no_face_detected" ||
            warning === "face_bbox_fallback_used",
        ),
      ));
  const appliedFiltersDescription = getAppliedFiltersDescription(
    result.appliedFilters,
  );

  return (
    <View style={styles.resultsCard}>
      <View style={[styles.resultsHeader, isMobile && styles.mobileStack]}>
        <View style={styles.resultsHeaderCopy}>
          <View
            style={
              faceReviewRequired ? styles.reviewBadge : styles.successBadge
            }
          >
            <Text
              style={
                faceReviewRequired
                  ? styles.reviewBadgeText
                  : styles.successBadgeText
              }
            >
              {isDebugMode
                ? "Debug sample ready"
                : faceReviewRequired
                  ? "Review required"
                  : hasFaceCensorship
                    ? "Detection complete"
                    : "Processing complete"}
            </Text>
          </View>
          <Text accessibilityRole="header" style={styles.resultsTitle}>
            {isDebugMode ? "Debug censorship editor" : "Image tools complete"}
          </Text>
          <Text style={styles.sectionDescription}>
            {hasEditableTools
              ? "Review every proposed region and correct any placement. All selected filters will be rendered at source resolution during export."
              : `Review the applied filters before export: ${appliedFiltersDescription}.`}
          </Text>
        </View>
      </View>

      <UnifiedImageToolsEditor
        result={result}
        imagePreviewUrls={imagePreviewUrls}
        placementsByImage={placementsByImage}
        faceStickerPlacementsByImage={faceStickerPlacementsByImage}
        faceBlurRegionsByImage={faceBlurRegionsByImage}
        downloadingIndex={downloadingIndex}
        isDownloadingAll={isDownloadingAll}
        isHighQualityExporting={isHighQualityExporting}
        editorsReady={editorsReady}
        viewportWidth={viewportWidth}
        showRegionDebugLabels={showRegionDebugLabels}
        onDownload={onDownload}
        onDownloadAll={onDownloadAll}
        onPlacementsChange={onPlacementsChange}
        onFaceStickerPlacementsChange={onFaceStickerPlacementsChange}
        onFaceBlurRegionsChange={onFaceBlurRegionsChange}
      />

      <Modal
        visible={downloadDialogVisible}
        transparent
        animationType="fade"
        onRequestClose={onCancelDownload}
      >
        <View style={styles.dialogBackdrop}>
          <View
            testID="image-tools-download-quality-dialog"
            accessibilityViewIsModal
            style={styles.dialog}
          >
            <Text accessibilityRole="header" style={styles.dialogTitle}>
              {requiresCompositeExport
                ? "Downloading image"
                : "Choose download quality"}
            </Text>
            <Text style={styles.sectionDescription}>
              {requiresCompositeExport
                ? "The filters are rendered at the original resolution and may take a moment to download."
                : "Regular download is available immediately. High quality is rendered at the original image resolution and may take longer."}
            </Text>
            {!hasFaceCensorship && isDebugMode ? (
              <Text style={styles.progressHint}>
                High-quality export requires an uploaded source image and is not
                available for the debug sample.
              </Text>
            ) : null}
            <View
              style={[
                styles.dialogActions,
                isMobile && styles.mobileDialogActions,
              ]}
            >
              <SecondaryButton
                text={
                  requiresCompositeExport || isHighQualityExporting
                    ? "Stop Waiting"
                    : "Cancel"
                }
                onPress={onCancelDownload}
                fullWidth={isMobile}
              />
              {!requiresCompositeExport ? (
                <SecondaryButton
                  text={
                    downloadDialogIsAll
                      ? "Regular Download All"
                      : "Regular Download"
                  }
                  onPress={onRegularDownload}
                  disabled={isHighQualityExporting}
                  fullWidth={isMobile}
                />
              ) : null}
              {requiresCompositeExport ? (
                <PrimaryButton
                  text="Downloading"
                  accessibilityLabel={
                    downloadDialogIsAll
                      ? "Downloading images"
                      : "Downloading image"
                  }
                  onPress={onHighQualityDownload}
                  loading
                  fullWidth={isMobile}
                />
              ) : (
                <PrimaryButton
                  text={
                    downloadDialogIsAll
                      ? "Download All in High Quality"
                      : "Download High Quality"
                  }
                  onPress={onHighQualityDownload}
                  loading={isHighQualityExporting}
                  disabled={isDebugMode || isHighQualityExporting}
                  fullWidth={isMobile}
                />
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};
